/**
 * "Device sent nothing for N days" (tri-server #1433).
 *
 * The server accepts this as a normal comparison condition on a pseudo-field:
 * `{ conditionType: 'Comparison', field: 'daysSinceLastUplink', operator: 'greater',
 * comparisonType: 'value', comparisonValue: N }` with N an integer >= 1.
 *
 * tri-model's `MeasurementType` enum cannot be extended from here (separately published package),
 * so the field is a portal-local constant that the field dropdown merges in. `ComparisonCondition`
 * stores `field` as a plain string, so it round-trips through `Condition.unmarshal` unchanged.
 */
import { ComparisonOperator, ComparisonType } from '@the-real-insight/tri-model';

export const NO_UPLINK_FIELD = 'daysSinceLastUplink';

/** The only operator the server accepts for the pseudo-field: "ist größer als >". */
export const NO_UPLINK_OPERATOR = ComparisonOperator.greater;

export const NO_UPLINK_COMPARISON_TYPE = ComparisonType.value;

export const isNoUplinkField = (field: unknown): boolean => field === NO_UPLINK_FIELD;

/** A whole number of days, at least 1 (no sign, no decimals). */
const DAYS_PATTERN = /^\d+$/;

/** Parses the days input; empty, fractional, negative or zero input yields `undefined`. */
export const parseDaysValue = (text: string): number | undefined => {
    const trimmed = text.trim();

    if (!DAYS_PATTERN.test(trimmed)) {
        return undefined;
    }

    const value = Number(trimmed);

    return Number.isSafeInteger(value) && value >= 1 ? value : undefined;
};

/** Whether a condition (model instance or plain JSON from the API) is the no-uplink condition. */
export const isNoUplinkCondition = (condition: any): boolean =>
    !!condition && condition.field === NO_UPLINK_FIELD && condition.conditionType !== 'AndCondition'
    && condition.conditionType !== 'OrCondition';

/** The days of the first no-uplink condition anywhere in the rule (also inside AND/OR). */
export const noUplinkDays = (conditions: any[] | undefined): number | undefined | null => {
    for (const condition of conditions ?? []) {
        if (isNoUplinkCondition(condition)) {
            return condition.comparisonValue ?? undefined;
        }

        const nested = noUplinkDays(condition?.conditions);

        if (nested !== null) {
            return nested;
        }
    }

    return null;
};

/** Whether any no-uplink condition sits inside an AND/OR group. */
export const hasNestedNoUplink = (conditions: any[] | undefined): boolean =>
    (conditions ?? []).some((condition) => Array.isArray(condition?.conditions)
        && (condition.conditions.some(isNoUplinkCondition) || hasNestedNoUplink(condition.conditions)));

/** Whether a no-uplink condition shares the rule with any other condition. */
export const noUplinkIsCombined = (conditions: any[] | undefined): boolean => {
    const list = conditions ?? [];

    return (list.length > 1 && list.some(isNoUplinkCondition)) || hasNestedNoUplink(list);
};
